import { useCallback, useEffect, useRef, useState } from "react";

import { orderBy } from "lodash";

import { type AirHandlerUnit, type BoilerRoom, type RawDataPoint } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import {
  toast,
  ActionButton,
  Button,
  CanAccess,
  Circle,
  Field,
  Modal,
  SearchField,
  Select,
  Switch,
  Typography,
} from "@eisox/design-system";
import { ArrowRightIcon, BracesIcon, CopyIcon, CrossIcon, UpdateIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useQuery } from "@tanstack/react-query";

import { moduleApi } from "~/api";
import { useClipboard } from "~/hooks/use-clipboard";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { getEquipmentModuleOptions, getModuleLabel } from "../helpers";
import { InitDataDialog } from "./init-data-dialog";

const NAME = "VariableDetailsDialog";

/** `name (a 10)` when sorted by name, `a 10 : name` when sorted by (type, instance) — the legacy layout. */
const formatVariable = (variable: RawDataPoint, orderedByName: boolean): { name: string; value: string | number } => {
  const { name = "-", type = "-", instance, value = "-" } = variable;
  const typeInstance = `${type.charAt(0)} ${instance ?? "-"}`;
  return { name: orderedByName ? `${name} (${typeInstance})` : `${typeInstance} : ${name}`, value };
};

type Equipment = Pick<BoilerRoom | AirHandlerUnit, "moduleId" | "name">;

interface VariableDetailsContentProps {
  buildingId: string;
  moduleId?: string;
  /** Building-wide entry point: the equipments of the page, whose modules are offered in the picker. */
  equipments?: Equipment[];
  /** Module the picker starts on — the one of the open page; falls back to the first option. */
  defaultModuleId?: string;
}

export const VariableDetailsContent: React.FC<VariableDetailsContentProps> = ({
  buildingId,
  moduleId,
  equipments = [],
  defaultModuleId,
}) => {
  const moduleOptions = getEquipmentModuleOptions(equipments);

  const { copyToClipboard } = useClipboard({
    onSuccess: () => toast.success(m.variable_details_copy_success()),
    onError: (error: string) => toast.error(m.variable_details_copy_error({ e: error })),
  });

  const [pickedModuleId, setPickedModuleId] = useState<string | undefined>(
    () => moduleOptions.find(option => option.value === defaultModuleId)?.value ?? moduleOptions[0]?.value,
  );
  const [orderedByName, setOrderedByName] = useState(true);
  const [search, setSearch] = useState("");
  const gridRef = useRef<HTMLDivElement>(null);
  const [numberCols, setNumberCols] = useState(1);

  const selectedModuleId = moduleId ?? pickedModuleId;

  const { data, isLoading, dataUpdatedAt, refetch } = useQuery({
    ...moduleApi.queries.rawData(buildingId, selectedModuleId ?? ""),
    enabled: !!selectedModuleId,
    // Each read is a gateway round-trip; don't refetch on remount/refocus — the dialog refreshes on demand.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const filtered = (data ?? []).filter(
    variable => !search || variable.name?.toLowerCase().includes(search.toLowerCase()),
  );
  const displayedVariables = orderedByName
    ? orderBy(filtered, "name", "asc")
    : orderBy(filtered, ["type", "instance"], ["asc", "asc"]);

  // Column-major fill needs an explicit row count, which needs the column count the grid actually resolved to.
  const measureCols = useCallback(() => {
    if (!gridRef.current) return;
    const columns = window.getComputedStyle(gridRef.current).gridTemplateColumns;
    setNumberCols(Math.max(1, columns.split(" ").filter(Boolean).length));
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const observer = new ResizeObserver(measureCols);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [measureCols]);

  const handleCopy = () => {
    const content = displayedVariables
      .map(variable => {
        const { name, value } = formatVariable(variable, orderedByName);
        return `${name}  ${value}`;
      })
      .join("\n");
    void copyToClipboard(content);
  };

  return (
    <div className="min-h-0 gap-5 flex w-full flex-1 flex-col">
      {!moduleId && moduleOptions.length > 1 && (
        <Field.Root>
          <Field.Label>{m.variable_details_module_label()}</Field.Label>
          <Select.Root multiple={false} value={pickedModuleId} onValueChange={setPickedModuleId}>
            <Select.Trigger disabled={isLoading}>
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {moduleOptions.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.name}
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
        </Field.Root>
      )}
      <div className="gap-5 flex justify-between">
        <div className="gap-2.5 flex items-center">
          <label className="m-0 text-base">{m.variable_details_sort_by_name()}</label>
          <Switch
            checked={orderedByName}
            onCheckedChange={() => setOrderedByName(!orderedByName)}
            disabled={isLoading}
          />
        </div>
        <div className="gap-3.75 flex items-center">
          <Button
            className={displayedVariables.length === 0 ? "text-primary-dark-gray" : "text-primary-blue"}
            onClick={handleCopy}
            disabled={displayedVariables.length === 0}
          >
            {m.variable_details_copy()} <CopyIcon />
          </Button>
          <div className="[&_p]:font-medium gap-1.25 flex items-center [&_p]:text-base [&_p]:text-primary-dark-gray [&_p]:italic">
            <p>{dataUpdatedAt ? dayjs(dataUpdatedAt).format("LTS") : undefined}</p>
            <Circle.Root
              size={22}
              backgroundColor="gray"
              color="darkGray"
              onClick={() => void refetch()}
              className={isLoading ? "cursor-default" : "cursor-pointer"}
            >
              <UpdateIcon />
            </Circle.Root>
          </div>
        </div>
      </div>
      <SearchField
        placeholder={m.variable_details_filter()}
        value={search}
        onValueChange={setSearch}
        disabled={isLoading}
      />
      <div
        ref={gridRef}
        className="gap-x-10 grid flex-1 grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] content-start overflow-x-hidden overflow-y-auto"
        style={{
          gridTemplateRows: `repeat(${Math.ceil(displayedVariables.length / numberCols)}, min-content)`,
        }}
      >
        {displayedVariables.length ? (
          displayedVariables.map((variable, index) => {
            const { name, value } = formatVariable(variable, orderedByName);
            return (
              <div key={index} className="gap-2.5 flex flex-row justify-between [&_p]:text-base">
                <Typography>{name}</Typography>
                <p>{value}</p>
              </div>
            );
          })
        ) : (
          <div className="col-span-full flex items-center justify-center">{m.variable_details_no_data()}</div>
        )}
      </div>
    </div>
  );
};

VariableDetailsContent.displayName = `${NAME}.Content`;

interface VariableDetailsDialogProps extends Modal.RootProps {
  buildingId: string;
  /** Scopes the dialog to one module (the "Debug automate" entry point) instead of offering a picker. */
  moduleId?: string;
  equipments?: Equipment[];
  defaultModuleId?: string;
  children?: React.ReactNode;
}

const VariableDetailsDialog: React.FC<VariableDetailsDialogProps> = ({
  buildingId,
  moduleId,
  equipments,
  defaultModuleId,
  open: openProp,
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { modules, boilerRooms, airHandlerUnits, permissions } = useBuildingContext(NAME);

  const module = moduleId ? modules.find(md => md.id === moduleId) : undefined;

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content className="flex h-[80vh] w-[85vw] flex-col">
        <Modal.Header
          title={m.variables_details()}
          subtitle={module ? getModuleLabel(module, boilerRooms, airHandlerUnits) : ""}
          icon={<BracesIcon />}
        >
          {/* Module-scoped only: the building-wide entry point has its own init action in the menu. */}
          {moduleId && (
            <CanAccess isAuthorized={permissions.module?.boilerroom?.variableData?.update}>
              <InitDataDialog buildingId={buildingId} moduleId={moduleId}>
                <ActionButton>
                  {m.init_data_by_bms_module()}
                  <ArrowRightIcon />
                </ActionButton>
              </InitDataDialog>
            </CanAccess>
          )}
          <Modal.Close asChild>
            <CrossIcon className="cursor-pointer" />
          </Modal.Close>
        </Modal.Header>
        {/* Only mount the content (and fire the gateway read) once the dialog is open. */}
        {open && (
          <VariableDetailsContent
            buildingId={buildingId}
            moduleId={moduleId}
            equipments={equipments}
            defaultModuleId={defaultModuleId}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};

VariableDetailsDialog.displayName = NAME;

export { VariableDetailsDialog };
export type { VariableDetailsDialogProps };
